import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@radix-ui/react-dropdown-menu";
import { primaryButtonCls, secondaryButtonCls } from "../../../components/ui-components/buttonStyles.js";
import Tooltip from "../../../components/form-components/Tooltip.jsx";
import { HeaderPortal, useSetHeaderRecordName } from "../../../components/ui-components/HeaderSlot.jsx";
import StatusPill from "./StatusPill";
import "../../../components/DropdownMenu.css";

// Overflow menu of lifecycle actions (pause/resume/archive). Uses the same dropdown primitives
// and menu styling as the report editor (DropdownMenu.css), with a trigger styled to match the
// HeaderButton next to it.
function ActionsMenu({ actions }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="More budget actions"
          // Compact secondary trigger — same border/height/radius tokens as secondaryButtonCls,
          // narrowed for an icon-only overflow control.
          className="inline-flex items-center justify-center box-border h-[36px] px-3 rounded-[14px] bg-white border border-solid border-btn-line text-btn-ink hover:bg-btn-fill-hover hover:border-btn-line-hover active:bg-btn-fill-active active:border-btn-line-active cursor-pointer transition-colors"
        >
          <FontAwesomeIcon
            icon={icon({ name: "angle-down", family: "sharp", style: "solid" })}
            className="text-sm"
          />
        </button>
      </DropdownMenuTrigger>
      {/* Portal to <body> so the menu escapes the TopBar's stacking context (sticky z-10) and
          renders above the page header (sticky z-40); otherwise the header's bottom border line
          paints over the open menu. */}
      <DropdownMenuPortal>
        <DropdownMenuContent className="DropdownMenuContent w-[200px]" side="bottom" align="end" sideOffset={6}>
          {actions.map((a) => (
            <DropdownMenuItem
              key={a.key}
              className="DropdownMenuItem cursor-pointer"
              disabled={a.disabled}
              onSelect={() => a.onSelect()}
            >
              {a.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
ActionsMenu.propTypes = { actions: PropTypes.array.isRequired };

const CockpitHeader = ({
  isEditMode,
  budgetName,
  status,
  activeSince,
  isSaving,
  dirty,
  onSaveDraft,
  onSaveAndApprove,
  onSubmitActivation,
  onPause,
  onResume,
  onArchive,
  saveDraftDisabled = false,
  saveAndApproveDisabled = false,
  saveDraftTooltip = null,
  saveAndApproveTooltip = null,
  showDraftUnderAllocatedHint = false,
  isTransitioning = false,
}) => {
  const navigate = useNavigate();

  // Header shows only the budget's name (or "Create budget" for a new one).
  const title = isEditMode ? budgetName || "..." : "Create budget";

  useSetHeaderRecordName(isEditMode ? (budgetName || null) : null);

  const draftDisabled = isSaving || saveDraftDisabled;
  const approveDisabled = isSaving || saveAndApproveDisabled;

  // Status-dependent action buttons (rendered into the global top bar).
  const renderActions = () => {
    // For new budgets or draft status: the create/draft flow (cancel + save draft + activate).
    if (!isEditMode || status === "draft") {
      return (
        <>
          <button type="button" className={secondaryButtonCls} onClick={() => navigate("/budgets")}>
            Cancel
          </button>
          <button
            id="save-draft-btn"
            type="button"
            className={secondaryButtonCls}
            onClick={onSaveDraft}
            disabled={draftDisabled}
            title={draftDisabled && saveDraftTooltip ? saveDraftTooltip : undefined}
            aria-describedby={showDraftUnderAllocatedHint ? "save-draft-tooltip" : undefined}
          >
            {isSaving ? "Saving..." : "Save draft"}
          </button>
          {onSubmitActivation && (
            <button
              id="submit-activation-btn"
              type="button"
              className={primaryButtonCls}
              onClick={onSubmitActivation}
              disabled={isTransitioning || saveAndApproveDisabled}
              title={saveAndApproveDisabled ? saveAndApproveTooltip : undefined}
            >
              {isTransitioning ? "Activating..." : "Activate budget"}
            </button>
          )}
          {!onSubmitActivation && (
            approveDisabled ? (
              <span
                data-tooltip-id="instantTooltip"
                data-tooltip-place="bottom"
                data-tooltip-content={
                  showDraftUnderAllocatedHint
                    ? "Draft can be saved while under-allocated."
                    : saveAndApproveTooltip ?? ""
                }
              >
                <button id="save-approve-btn" type="button" className={primaryButtonCls} onClick={onSaveAndApprove} disabled={approveDisabled}>
                  {isSaving ? "Saving..." : "Save & request approval"}
                </button>
              </span>
            ) : (
              <button id="save-approve-btn" type="button" className={primaryButtonCls} onClick={onSaveAndApprove} disabled={approveDisabled}>
                {isSaving ? "Saving..." : "Save & request approval"}
              </button>
            )
          )}
        </>
      );
    }

    // Edit mode, saved budget (pending_approval / active / paused / archived): one primary
    // "Save changes" (styled like the hierarchy page's "Create draft") + an overflow menu with
    // the available lifecycle actions.
    const menuActions = [];
    if (onResume) menuActions.push({ key: "resume", label: isTransitioning ? "Resuming..." : "Resume budget", onSelect: onResume, disabled: isTransitioning });
    if (onPause) menuActions.push({ key: "pause", label: isTransitioning ? "Pausing..." : "Pause budget", onSelect: onPause, disabled: isTransitioning });
    if (onArchive) menuActions.push({ key: "archive", label: isTransitioning ? "Archiving..." : "Archive budget", onSelect: onArchive, disabled: isTransitioning });

    return (
      <>
        <button
          id="save-btn"
          type="button"
          onClick={onSaveDraft}
          disabled={!dirty || isSaving}
          className={primaryButtonCls}
        >
          {isSaving ? "Saving..." : "Save changes"}
        </button>
        {menuActions.length > 0 && <ActionsMenu actions={menuActions} />}
      </>
    );
  };

  return (
    <>
      {/* Title + status live in the header's row-1 title slot. */}
      <HeaderPortal slot="title">
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-[20px] leading-[1.4] font-extrabold tracking-[-0.015em] text-text truncate font-['Plus_Jakarta_Sans',sans-serif]">
            {title}
          </div>
          {isEditMode && status && <StatusPill status={status} activeSince={activeSince} />}
          {dirty && (
            <span className="text-xs text-warning bg-warning-soft px-2 py-0.5 rounded-ctrl font-medium select-none whitespace-nowrap">
              Unsaved changes
            </span>
          )}
        </div>
      </HeaderPortal>

      {/* Action buttons live in the header's row-1 actions slot. */}
      <HeaderPortal slot="actions">
        <div className="flex items-center gap-2.5">
          {renderActions()}
          <Tooltip />
        </div>
      </HeaderPortal>
    </>
  );
};

CockpitHeader.propTypes = {
  isEditMode: PropTypes.bool,
  budgetName: PropTypes.string,
  status: PropTypes.string,
  activeSince: PropTypes.string,
  isSaving: PropTypes.bool,
  dirty: PropTypes.bool,
  onSaveDraft: PropTypes.func.isRequired,
  onSaveAndApprove: PropTypes.func.isRequired,
  onSubmitActivation: PropTypes.func,
  onPause: PropTypes.func,
  onResume: PropTypes.func,
  onArchive: PropTypes.func,
  saveDraftDisabled: PropTypes.bool,
  saveAndApproveDisabled: PropTypes.bool,
  saveDraftTooltip: PropTypes.string,
  saveAndApproveTooltip: PropTypes.string,
  showDraftUnderAllocatedHint: PropTypes.bool,
  isTransitioning: PropTypes.bool,
};

export default CockpitHeader;
